import store from "../redux-store";
import {
  addDiscountApprovalData,
  applyDiscountApi,
  getExternalDiscounts,
} from "../redux-store/slices/DiscountSlice";
import {
  externalCouponRefId,
  externalOfferVerify,
  redeemPoints,
} from "../redux-store/slices/Loyalty";
import { saveSaleData } from "../redux-store/slices/SaleSlice";
import { getClonedSaleDataFromReducer } from "../helpers/saleHelpers";
import { repriceSale } from "../ordering/writePath";
import {
  buildApplyRequest,
  classifyVerifyResponse,
  getApplyPlan,
  getVerifyPlan,
} from "./external";
import { normaliseExternalOffers } from "./catalog";
import { resolveDiscountProvider } from "./provider";
import { dropDiscount } from "./release";
import {
  selectActiveCouponProvider,
  selectAppliedDiscountInfo,
  selectExternalReferenceId,
  selectProviderByMetaId,
  selectSaleData,
} from "./selectors";
import type { ApplyPlan, BillDiscount, DiscountOffer, Sale, VerifyOutcome } from "./types";

/**
 * The discount flow's IMPERATIVE ADAPTER — the only file here that dispatches.
 *
 * Layering, innermost first. Each layer may use the ones above it and nothing below:
 *
 * ```
 * types · constants          shapes and vocabulary
 * providers/                 what ONE partner can do — data, not code paths
 * provider                   pure rules over a partner
 * applicability · catalog     pure rules over an offer and a bill
 * billDiscount · external     pure rules over a bill entry and a partner conversation
 * approval                    pure rules over a manager approval
 * selectors                   the only file naming a redux key
 * index (this file)           dispatch + payload assembly
 * hooks/                      what UI actually calls
 * ```
 *
 * UI should reach for `./hooks` first; these functions exist for callers that are not React —
 * a saga, a middleware effect, another adapter.
 *
 * ⚠️ Every store read happens INSIDE a function. Nothing at module-eval time crosses into a
 * module that is in a cycle with the store (audit checks H/J).
 */

/* ── the offers list ───────────────────────────────────────────────────────────────────── */

/**
 * Fetch the partner offers available to this customer against the CURRENT sale.
 *
 * The answer is per-SALE, not per-customer: adding an item can change which offers qualify, so
 * the offers are handed to the callback rather than cached in a slice that would have to be
 * swept.
 *
 * ⚠️ Gate the call on `isExternalCouponProvider(provider?.metaId)` — a partner with no offers
 * leg has nothing to answer with. `useDiscountCatalog` does.
 *
 * The offers arrive NORMALISED (`normaliseExternalOffers`): the API keys them under four
 * different names and the rest of the flow indexes by `discountId`.
 */
export function fetchExternalOffers(
  provider: { mid?: string; metaId?: string } | null | undefined,
  cb: (offers: DiscountOffer[]) => void,
): void {
  store.dispatch(
    getExternalDiscounts({
      data: {
        providerId: provider?.mid,
        metaId: provider?.metaId,
        sale: getClonedSaleDataFromReducer(),
      },
      callback: (response: any) => cb(normaliseExternalOffers(response?.discounts)),
    }),
  );
}

/* ── a typed coupon code ───────────────────────────────────────────────────────────────── */

/**
 * Validate a coupon code against the current sale.
 *
 * The answer lands on `discountStore.discountInfo` (via the saga's `applyDiscountSuccess`), NOT
 * in a callback: the API expands one code into a LIST of discounts, and the surface then applies
 * them. `useDiscountBoard` watches that slot.
 *
 * ⚠️ `customerPhone` is DELETED rather than sent empty — a customer-bound coupon validated
 * against a blank phone number is refused, and the sale may legitimately have no customer.
 *
 * @param sale - the bill to validate against. Pass one explicitly for a single-slot surface,
 *   where the candidate bill has the previous offer already stripped.
 */
export function validateCouponCode(
  code: string,
  {
    ristaDiscount,
    sale,
    onError,
  }: { ristaDiscount?: boolean; sale?: Sale; onError?: () => void } = {},
): void {
  if (!code) return;
  const state = store.getState();
  const data: Record<string, any> = {
    couponCode: code,
    sale: sale ?? selectSaleData(state),
    customerPhone: (selectSaleData(state) as any)?.customerInfo?.contactInfo?.phoneNumber,
    ristaDiscount,
  };
  if (!data.customerPhone) delete data.customerPhone;
  store.dispatch(applyDiscountApi({ data, errorCallback: onError }));
}

/* ── the partner conversation ──────────────────────────────────────────────────────────── */

/**
 * The bill's customer, sent BESIDE the post body on the apply and verify legs (web
 * `DiscountDeals`, `48827daef`): the loyalty saga refreshes that customer after a DotPe apply or
 * verify (`refreshCustomerAfterLoyaltyReward`, which gates on `postBody.metaId`), so their balance
 * and milestone rows are not stale. Web also sends `isLoyaltyReward`, which nothing reads since
 * DM-146, so it is not repeated.
 */
const customerRefIdOf = (state: unknown): string | undefined =>
  (selectSaleData(state as any) as any)?.customerInfo?.customerRefId;

/**
 * Ask the partner to apply one of its offers, and classify what comes back.
 *
 * 🔴 **The caller gets an `ApplyPlan`, never the raw response.** Which of the three outcomes a
 * response is depends on the partner's profile — whether it uses an OTP leg at all, whether this
 * offer may waive it, whether this leg carries a `referenceId` — and pairing the request with
 * the reading of its answer is this function's whole job. Every surface that did it inline got
 * some of it wrong; see `getApplyPlan`.
 *
 * The `referenceId` a partner issues on THIS leg is stored before the callback runs, because the
 * verify request quotes it.
 *
 * @param sale - the bill to apply against. Pass one explicitly when replacing an existing offer,
 *   so the partner prices against the bill without it.
 */
export function applyExternalOffer(
  offer: DiscountOffer,
  cb: (plan: ApplyPlan) => void,
  { sale }: { sale?: Sale } = {},
): void {
  const state = store.getState();
  const provider = selectActiveCouponProvider(state);
  store.dispatch(
    redeemPoints({
      postBody: buildApplyRequest(offer, {
        sale: sale ?? getClonedSaleDataFromReducer(),
        providerId: provider?.mid,
        metaId: provider?.metaId,
        appliedDiscountInfo: selectAppliedDiscountInfo(state),
      }),
      customerRefId: customerRefIdOf(state),
      callback: (response: any) => {
        const plan = getApplyPlan(response, offer, provider?.metaId);
        if (plan.kind !== "failed" && plan.referenceId) {
          storeExternalReferenceId(plan.referenceId);
        }
        cb(plan);
      },
    }),
  );
}

/**
 * Verify the OTP the partner sent, and classify what comes back.
 *
 * 🔴 **Both the payload SHAPE and the ACTION are chosen by the partner's profile**, together, by
 * `getVerifyPlan` — a nested body on the offer endpoint, or a flat one on the loyalty endpoint,
 * is rejected as malformed. The old component kept the two in one ternary, so a partner that
 * needed one and not the other could not be expressed.
 *
 * ⚠️ The partner may resolve the coupon against a DIFFERENT provider than the active one: the
 * offer's `couponProvider` wins, and its `mid` is looked up from the provider list. A bill can
 * carry an offer from a partner the merchant has since switched away from.
 */
export function verifyExternalOffer(
  offer: DiscountOffer,
  otp: string,
  cb: (outcome: VerifyOutcome) => void,
): void {
  const state = store.getState();
  const activeProvider = selectActiveCouponProvider(state);
  const metaId = resolveDiscountProvider(offer, activeProvider?.metaId);
  const providerId = selectProviderByMetaId(state, offer?.couponProvider)?.mid || activeProvider?.mid;

  const { channel, body } = getVerifyPlan(offer, otp, {
    sale: getClonedSaleDataFromReducer(),
    providerId,
    metaId,
    referenceId: selectExternalReferenceId(state),
    appliedDiscountInfo: selectAppliedDiscountInfo(state),
  });

  const action = channel === "loyalty-redeem" ? redeemPoints : externalOfferVerify;
  store.dispatch(
    action({
      postBody: body,
      customerRefId: customerRefIdOf(state),
      callback: (response: any) => {
        const outcome = classifyVerifyResponse(response, metaId);
        if (outcome.kind === "verified" && outcome.referenceId) {
          storeExternalReferenceId(outcome.referenceId);
        }
        cb(outcome);
      },
    }),
  );
}

/**
 * Record the partner reference id later calls must quote.
 *
 * ⚠️ A SINGLE slot on the loyalty slice, shared by every partner — which is why
 * `referenceIdSource` exists. Only ever called with an id the profile says that leg carries;
 * writing one from the other leg overwrites a good id with `undefined` and leaves the removal
 * unblock with nothing to send.
 */
export function storeExternalReferenceId(referenceId: string): void {
  store.dispatch(externalCouponRefId(referenceId));
}

/* ── writing to the bill ───────────────────────────────────────────────────────────────── */

/**
 * Price a candidate bill WITHOUT committing it, and hand back the priced sale.
 *
 * 🔴 The whole apply path depends on this being non-committing: the flow prices, asks
 * `readApplyResult` whether the engine honoured the discount, and only then saves. Since
 * 2026-10-06 it prices through the shared write path (`ordering/writePath`) on both platforms.
 */
export function priceSale(sale: Sale): Sale {
  return repriceSale(sale, { commit: false }) as Sale;
}

/**
 * Price a bill with NOTHING written — not the sale, not approval state.
 *
 * 🔴 `priceSale`'s `commit: false` stops the sale being committed but NOT the approval-state
 * reset inside the write path (`ordering/writePath`), which dispatches off whatever bill it was
 * handed. For the apply flow that is fine (one candidate, about to be committed); for a caller that prices many
 * hypothetical bills to ask which offers the engine would honour, it is a write per probe — and
 * it trips on precisely the refused offers such a caller is looking for. See the `dryRun` note
 * on `repriceSale`, including the coupon sweep a dry run still performs (L50).
 *
 * Use this ONLY to ask a question. `priceSale` remains the one to use when the answer is going
 * to be committed.
 */
export function priceSaleDryRun(sale: Sale): Sale {
  return repriceSale(sale, { commit: false, dryRun: true }) as Sale;
}

/** Price a bill AND commit it. For a removal, where there is nothing to verify first. */
export function priceAndSaveSale(sale: Sale): Sale {
  return repriceSale(sale) as Sale;
}

/** Commit a sale the flow has already decided on. */
export function commitSale(sale: Sale): void {
  store.dispatch(saveSaleData(sale));
}

/**
 * Take one discount off the bill and commit the result: `dropDiscount` (`./release`: the partner
 * release, the strip and the coupon answer's reset, in that order) and then price and save.
 */
export function removeDiscount(discount: BillDiscount, sale?: Sale): Sale {
  return priceAndSaveSale(dropDiscount(discount, sale ?? selectSaleData(store.getState())));
}

/* ── manager approval ──────────────────────────────────────────────────────────────────── */

/** Record the approval state this bill's discounts imply. */
export function recordApprovalState(approvalData: Record<string, any>): void {
  store.dispatch(addDiscountApprovalData(approvalData as any));
}

export { dropDiscount, releaseExternalCoupon } from "./release";

/* ── public surface ────────────────────────────────────────────────────────────────────── */

export * from "./types";
export * from "./constants";
export * from "./providers";
export * from "./provider";
export * from "./applicability";
export * from "./catalog";
export * from "./billDiscount";
export * from "./external";
export * from "./approval";
export * from "./selectors";
export * from "./itemLevelDiscounts";
